import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import { BugoData } from '@shared/api/bugo/bugo.interface';
import { CourtesyContent } from '@shared/api/courtesyAsset/courtesyAsset.interface';
import Tabs from '@shared/components/Tabs';
import Select from '@shared/containers/Select/Select';
import { notificateSuccess } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useForm, useWatch } from 'react-hook-form';

import {
  courtesyContentAtom,
  courtesyContentStringAtom,
  courtesyEditRequestAtom,
  useDeceasedNameAtom,
} from './courtesy.atom';
import { getCourtesyStringFromContent } from './courtesyGenerator';

interface Props {
  courtesyContentList: CourtesyContent[];
  bugo: BugoData;
  // 기간이 지난 부고: 고인명·상주명·관계를 노출하지 않는다.
  anonymous?: boolean;
}

//* 해당 멤버가 답례글에서 예문 선택기
export const MemberCourtesyContentSelector: FC<Props> =
  function MemberCourtesyContentSelector({
    courtesyContentList,
    bugo,
    anonymous = false,
  }: Props) {
    const [courtesyContent, setCourtesyContent] = useRecoilState(courtesyContentAtom);

    const [courtesyContentString, setCourtesyContentString] = useRecoilState(
      courtesyContentStringAtom,
    );

    const [useDeceasedName, setUseDeceasedName] = useRecoilState(useDeceasedNameAtom);
    //!reverseType (익명 모드에선 관계도 감추므로 고인명 표시 토글 자체를 없앤다)
    const reverseType = anonymous ? undefined : bugo?.member?.reverseType;
    const hasReverseType = !!reverseType;

    const deafaultCourtesyContentId = bugo?.fevent?.courtesyContent;

    const defaultCourtesyContent = courtesyContentList.find((item) => {
      return item._id === deafaultCourtesyContentId;
    });

    // !초기값 courtesyContent  -> defaultCourtesyContent(fevent) -> 0번째
    const { control, setValue } = useForm({
      defaultValues: {
        courtesyContent:
          courtesyContent?.name ??
          defaultCourtesyContent?.name ??
          courtesyContentList[0]?.name,
        courtesyContentString: courtesyContent
          ? getCourtesyStringFromContent(
              courtesyContent,
              bugo,
              useDeceasedName,
              anonymous,
            )
          : defaultCourtesyContent
          ? getCourtesyStringFromContent(
              defaultCourtesyContent,
              bugo,
              useDeceasedName,
              anonymous,
            )
          : courtesyContentList[0]
          ? getCourtesyStringFromContent(
              courtesyContentList[0],
              bugo,
              useDeceasedName,
              anonymous,
            )
          : '',
      },
    });

    const courtesyContentSetList = useMemo(() => {
      const courtesyContentCategory = courtesyContentList.map((item) => {
        return item.name.split('(')[0];
      });
      const categorySet = Array.from(new Set(courtesyContentCategory)) as string[];
      return categorySet;
    }, [courtesyContentList]);

    // !tab 초기값  default(fevent)
    const [selectedTab, setSelectedTab] = useState<number>(
      // index === -1이면 0 처리
      Math.max(
        courtesyContentSetList.indexOf(defaultCourtesyContent?.name?.split('(')[0] ?? ''),
        0,
      ),
    );

    // 해당 탭 클릭시 해당 탭에서 선택 가능한 예문 리스트
    const categoriedCourtesyContentList = courtesyContentList.filter(
      (courtesyContent) => {
        return courtesyContent.name.split('(')[0] === courtesyContentSetList[selectedTab];
      },
    );

    // 해당 탭 클릭시 첫번째걸로 courtesyContent 변경
    const onTabClick = (tab: number) => {
      const tabCourtesyCotentList = courtesyContentList.filter((courtesyContent) => {
        return courtesyContent.name.split('(')[0] === courtesyContentSetList[tab];
      });
      setValue('courtesyContent', tabCourtesyCotentList[0]?.name);
    };

    const courtesyContentName = useWatch({ control, name: 'courtesyContent' });

    //! control courtesyContent 변경시 recoilState CourtesyContent courtesyContentString 변경되도록함

    useEffect(() => {
      const courtesyContentByName = courtesyContentList.find((item) => {
        return item.name === courtesyContentName;
      });
      if (courtesyContentByName && courtesyContent?.name !== courtesyContentByName.name) {
        setCourtesyContent(courtesyContentByName);

        setCourtesyContentString(
          getCourtesyStringFromContent(
            courtesyContentByName,
            bugo,
            useDeceasedName,
            anonymous,
          ),
        );
      }
    }, [
      anonymous,
      bugo,
      courtesyContent,
      courtesyContentList,
      courtesyContentName,
      setCourtesyContent,
      setCourtesyContentString,
      useDeceasedName,
    ]);

    useEffect(() => {
      if (courtesyContent) {
        setCourtesyContentString(
          getCourtesyStringFromContent(courtesyContent, bugo, useDeceasedName, anonymous),
        );
      }
    }, [anonymous, bugo, courtesyContent, setCourtesyContentString, useDeceasedName]);

    //* 편집 진입점을 명시 버튼으로 제공한다 (글자 크기를 키운 어르신이 힌트 문구를 못 찾는 문제)
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [editing, setEditing] = useState(false);

    //* 기기 글자 크기를 키워도 내부 스크롤로 글이 잘리지 않도록 내용 높이에 맞춰 늘린다.
    useEffect(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.style.height = 'auto';
      // scrollHeight는 테두리를 빼고 재므로 테두리 두께만큼 더해준다 (편집중엔 border-2)
      const borderHeight = el.offsetHeight - el.clientHeight;
      el.style.height = `${el.scrollHeight + borderHeight}px`;
    }, [courtesyContentString, editing]);

    //* 미리보기 모달에서 "글 수정하러 가기"를 누르면 편집란으로 이동시킨다.
    const editRequest = useRecoilValue(courtesyEditRequestAtom);

    useEffect(() => {
      if (!editRequest) return;
      const el = textareaRef.current;
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
      el.setSelectionRange(el.value.length, el.value.length);
    }, [editRequest]);

    const notificationInstance = useRecoilValue(notificationInstanceAtom);

    //* "수정 완료"를 누르면 클릭보다 blur가 먼저 나므로, 편집 상태 해제를 잠깐 미뤄
    //* 버튼 클릭 시점에도 편집 중으로 인식되게 한다.
    const blurTimerRef = useRef<number | null>(null);

    useEffect(() => {
      return () => {
        if (blurTimerRef.current) window.clearTimeout(blurTimerRef.current);
      };
    }, []);

    const onTextareaFocus = () => {
      if (blurTimerRef.current) window.clearTimeout(blurTimerRef.current);
      setEditing(true);
    };

    const onTextareaBlur = () => {
      if (blurTimerRef.current) window.clearTimeout(blurTimerRef.current);
      blurTimerRef.current = window.setTimeout(() => setEditing(false), 200);
    };

    const onEditButtonClick = () => {
      const el = textareaRef.current;
      if (!el) return;
      if (editing) {
        if (blurTimerRef.current) window.clearTimeout(blurTimerRef.current);
        setEditing(false);
        el.blur();
        notificateSuccess(notificationInstance, '답례글이 수정되었습니다.');
        return;
      }
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    };

    //* 예문 그대로인지(=고칠 게 없는지) 판단해 초기화 버튼 노출을 정한다.
    const generatedContentString = courtesyContent
      ? getCourtesyStringFromContent(courtesyContent, bugo, useDeceasedName, anonymous)
      : null;
    const isEdited =
      generatedContentString !== null &&
      (courtesyContentString ?? '') !== generatedContentString;

    //* 편집 후 되돌릴 수 있는 탈출구
    const onResetClick = () => {
      if (generatedContentString === null) return;
      setCourtesyContentString(generatedContentString);
      notificateSuccess(notificationInstance, '처음 문구로 되돌렸습니다.');
    };

    return (
      <>
        <div className="bg-white pt-4">
          <div className="px-4 pb-2">
            <h1 className="bugo-h1 flex items-center gap-2">
              <span className="center-box h-6 w-6 shrink-0 rounded-full text-13 font-bold text-white theme-bg-main">
                2
              </span>
              인사말 고르기
            </h1>
            <p className="mt-1 text-13 text-gray-500">
              종교·상황에 맞는 것을 누르면 아래 내용이 바뀝니다
            </p>
          </div>
          <Tabs
            items={courtesyContentSetList}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            onTabClick={onTabClick}
          />
        </div>
        {/* 같은 종류 안에 예문이 여럿일 때만 보조 선택을 보여준다 (한 개면 고를 게 없다) */}
        {categoriedCourtesyContentList.length > 1 && (
          <div className="px-4 text-sm font-medium">
            <p className="mb-1 text-13 font-medium text-gray-500">
              같은 종류의 다른 문구 보기
            </p>
            <Select
              name="courtesyContent"
              placeholder={'예문 선택'}
              control={control}
              optionList={categoriedCourtesyContentList.map((item) => {
                return item.name;
              })}
              defaultValue={categoriedCourtesyContentList[0]?.name}
            ></Select>
          </div>
        )}
        <div className="px-4">
          <div className="mb-1 flex items-end justify-between gap-2">
            <h2 className="bugo-h1 mb-1.5 flex items-center gap-2">
              <span className="center-box h-6 w-6 shrink-0 rounded-full text-13 font-bold text-white theme-bg-main">
                3
              </span>
              내용 확인하기
            </h2>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                // 버튼을 눌러도 textarea 포커스가 먼저 풀리지 않도록 막는다
                onMouseDown={(e) => e.preventDefault()}
                onClick={onEditButtonClick}
                className="button-sm center-box min-h-[32px] gap-1 rounded-md border-0 bg-gray-800 px-2 text-14 font-bold text-white"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                <span>{editing ? '수정 완료' : '글 수정하기'}</span>
              </button>
              {isEdited && (
                <button
                  type="button"
                  onClick={onResetClick}
                  className="button-sm center-box min-h-[32px] rounded-md border px-2 text-14 font-medium text-gray-600"
                >
                  초기화
                </button>
              )}
            </div>
          </div>
          {hasReverseType && (
            <label className="mb-2 flex cursor-pointer items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5">
              <span className="block text-14 font-bold text-gray-800">
                고인 성함 표시
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={useDeceasedName}
                onClick={() => setUseDeceasedName((prev) => !prev)}
                className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${
                  useDeceasedName ? 'theme-bg-main' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-6 w-6 rounded-full bg-white shadow transition-transform ${
                    useDeceasedName ? 'translate-x-[22px]' : 'translate-x-[2px]'
                  }`}
                />
              </button>
            </label>
          )}
          <textarea
            ref={textareaRef}
            className={`min-h-[328px] w-full resize-none overflow-hidden bg-white px-4 py-3 text-16 leading-relaxed ${
              editing ? 'border-2 border-gray-800' : 'border border-gray-300'
            }`}
            value={courtesyContentString ?? ''}
            onFocus={onTextareaFocus}
            onBlur={onTextareaBlur}
            onChange={(e) => {
              setCourtesyContentString(e.target.value);
            }}
          ></textarea>
        </div>
      </>
    );
  };
